import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, List, ListItemButton, Tooltip as MuiTooltip, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ConstructionIcon from "@mui/icons-material/Construction";
import { useSnackbar } from "notistack";
import { CircleMarker, MapContainer, Popup, TileLayer, Tooltip, useMap } from "react-leaflet";
import type { CircleMarker as LeafletCircleMarker, Map as LeafletMap } from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "../../api/axios";
import { ERROR_SNACKBAR, TECHNICIAN_LOCATIONS_ENDPOINT } from "../../consts";
import { formatDateTimeInline } from "../../utils";
import CustomCircularProgress from "../../components/ui/customCircularProgress/customCircularProgress.component";

export interface TechnicianLocation {
  id: number;
  email: string;
  full_name: string;
  latitude: number;
  longitude: number;
  accuracy: number | null;
  recorded_at: string;
  // "gps" - the last fix the technician's browser sent. "job" - they don't
  // share their location (or haven't since), so they're placed at the address
  // of the job they last moved to "In progress"; recorded_at is when.
  source: "gps" | "job";
  job_id: number | null;
  job_number: string | null;
}

type TechnicianLocationsResponse = {
  count: number;
  results: TechnicianLocation[];
};

type FreshnessBucket = {
  maxMinutes: number;
  color: string;
  opacity: number;
  labelKey: string;
};

// Ordered from freshest to oldest: markers fade from green to gray as the fix ages.
const FRESHNESS_BUCKETS: FreshnessBucket[] = [
  { maxMinutes: 15, color: "#2e7d32", opacity: 1, labelKey: "Current (up to 15 min)" },
  { maxMinutes: 60, color: "#66bb6a", opacity: 0.95, labelKey: "Up to 1 hour" },
  { maxMinutes: 6 * 60, color: "#8fa98f", opacity: 0.85, labelKey: "Up to 6 hours" },
  { maxMinutes: 24 * 60, color: "#9e9e9e", opacity: 0.7, labelKey: "Up to 24 hours" },
  { maxMinutes: Infinity, color: "#bdbdbd", opacity: 0.5, labelKey: "Older than 24 hours" },
];

const POLAND_CENTER: [number, number] = [52.07, 19.48];
const DEFAULT_ZOOM = 6;
const REFRESH_INTERVAL_MS = 60 * 1000;
// Relative ages ("5 min ago") are recomputed this often between fetches.
const CLOCK_TICK_MS = 30 * 1000;

const minutesSince = (recordedAt: string, now: number) =>
  Math.max(0, (now - new Date(recordedAt).getTime()) / 60000);

export const getLocationFreshness = (recordedAt: string, now: number = Date.now()): FreshnessBucket => {
  const minutes = minutesSince(recordedAt, now);
  return FRESHNESS_BUCKETS.find((bucket) => minutes <= bucket.maxMinutes) ?? FRESHNESS_BUCKETS[FRESHNESS_BUCKETS.length - 1];
};

const formatAge = (recordedAt: string, now: number, t: (key: string, options?: { value: number }) => string) => {
  const minutes = Math.floor(minutesSince(recordedAt, now));
  if (minutes < 1) return t("just now");
  if (minutes < 60) return t("{{value}} min ago", { value: minutes });
  if (minutes < 24 * 60) return t("{{value}} h ago", { value: Math.floor(minutes / 60) });
  return t("{{value}} d ago", { value: Math.floor(minutes / (24 * 60)) });
};

/** Sizes the map once the modal has faded in, and frames the markers on first load. */
const MapViewport = ({ locations }: { locations: TechnicianLocation[] }) => {
  const map = useMap();
  const fittedRef = useRef(false);

  useEffect(() => {
    // Leaflet measures its container on mount, while the modal is still
    // animating in - re-measure once it has settled.
    const timeoutId = window.setTimeout(() => map.invalidateSize(), 400);
    return () => window.clearTimeout(timeoutId);
  }, [map]);

  useEffect(() => {
    // Only frame the markers once, so periodic refreshes don't undo the
    // admin's own panning and zooming.
    if (fittedRef.current || locations.length === 0) return;
    fittedRef.current = true;
    map.fitBounds(
      locations.map((location) => [location.latitude, location.longitude] as [number, number]),
      { padding: [40, 40], maxZoom: 14 }
    );
  }, [map, locations]);

  return null;
};

const FreshnessDot = ({ bucket, size = 12 }: { bucket: FreshnessBucket; size?: number }) => (
  <Box
    component="span"
    sx={{
      width: size,
      height: size,
      borderRadius: "50%",
      backgroundColor: bucket.color,
      opacity: bucket.opacity,
      border: "2px solid #fff",
      boxShadow: "0 0 0 1px rgba(0,0,0,0.25)",
      display: "inline-block",
      flexShrink: 0,
    }}
  />
);

export const TechnicianLocationsMap = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [locations, setLocations] = useState<TechnicianLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());
  const mapRef = useRef<LeafletMap | null>(null);
  const markerRefs = useRef<Record<number, LeafletCircleMarker | null>>({});

  const fetchLocations = useCallback(async () => {
    try {
      const response = await axios.get<TechnicianLocationsResponse>(TECHNICIAN_LOCATIONS_ENDPOINT);
      setLocations(response.data.results);
    } catch {
      enqueueSnackbar(t("Failed to fetch technician locations"), ERROR_SNACKBAR);
    } finally {
      setLoading(false);
      setNow(Date.now());
    }
  }, [enqueueSnackbar, t]);

  useEffect(() => {
    void fetchLocations();
    const refreshId = window.setInterval(() => void fetchLocations(), REFRESH_INTERVAL_MS);
    const clockId = window.setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
    return () => {
      window.clearInterval(refreshId);
      window.clearInterval(clockId);
    };
  }, [fetchLocations]);

  const focusTechnician = (location: TechnicianLocation) => {
    mapRef.current?.flyTo([location.latitude, location.longitude], Math.max(mapRef.current.getZoom(), 14));
    markerRefs.current[location.id]?.openPopup();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        {FRESHNESS_BUCKETS.map((bucket) => (
          <Box key={bucket.labelKey} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <FreshnessDot bucket={bucket} />
            <Typography variant="caption" color="text.secondary">
              {t(bucket.labelKey)}
            </Typography>
          </Box>
        ))}
        <Box sx={{ flex: 1 }} />
        <MuiTooltip title={t("Refresh")}>
          <IconButton size="small" onClick={() => void fetchLocations()} aria-label={t("Refresh")}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </MuiTooltip>
      </Box>

      <Box
        sx={{
          position: "relative",
          height: { xs: "50vh", md: "60vh" },
          borderRadius: "var(--defaultRadius)",
          overflow: "hidden",
          border: 1,
          borderColor: "divider",
          // Leaflet's popups and tooltips are always white, while the MUI text
          // inside them follows the theme - match them to the theme instead,
          // so the text stays readable in dark mode.
          "& .leaflet-popup-content-wrapper, & .leaflet-popup-tip, & .leaflet-tooltip": {
            backgroundColor: "background.paper",
            color: "text.primary",
            borderColor: "divider",
          },
          "& .leaflet-tooltip-top::before": {
            borderTopColor: (theme) => theme.palette.background.paper,
          },
          "& .leaflet-container a.leaflet-popup-close-button": {
            color: "text.secondary",
          },
        }}
      >
        <MapContainer ref={mapRef} center={POLAND_CENTER} zoom={DEFAULT_ZOOM} style={{ height: "100%", width: "100%" }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapViewport locations={locations} />
          {/* Oldest first, so the freshest markers are drawn on top. */}
          {[...locations].reverse().map((location) => {
            const bucket = getLocationFreshness(location.recorded_at, now);
            return (
              <CircleMarker
                key={location.id}
                ref={(marker) => {
                  markerRefs.current[location.id] = marker;
                }}
                center={[location.latitude, location.longitude]}
                radius={10}
                pathOptions={{
                  color: "#ffffff",
                  weight: 2,
                  fillColor: bucket.color,
                  fillOpacity: bucket.opacity,
                  opacity: Math.max(bucket.opacity, 0.7),
                }}
              >
                <Tooltip direction="top" offset={[0, -8]}>
                  {location.full_name}
                </Tooltip>
                <Popup>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {location.full_name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: bucket.color, fontWeight: 600 }}>
                    {formatAge(location.recorded_at, now, t)}
                  </Typography>
                  {location.source === "job" ? (
                    <>
                      <Typography
                        variant="caption"
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        sx={{ mt: 0.5 }}
                      >
                        <ConstructionIcon sx={{ fontSize: 14 }} />
                        {t("Location from the latest job")}: {location.job_number}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t("Job started")}: {formatDateTimeInline(location.recorded_at)}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t("Location updated")}: {formatDateTimeInline(location.recorded_at)}
                    </Typography>
                  )}
                  {location.accuracy != null && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t("Accuracy")}: ±{Math.round(location.accuracy)} m
                    </Typography>
                  )}
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.5)",
              zIndex: 1000,
            }}
          >
            <CustomCircularProgress />
          </Box>
        )}
      </Box>

      {!loading && locations.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("No technician locations yet")}
        </Typography>
      ) : (
        <List disablePadding dense sx={{ maxHeight: 220, overflowY: "auto" }}>
          {locations.map((location) => {
            const bucket = getLocationFreshness(location.recorded_at, now);
            return (
              <ListItemButton
                key={location.id}
                onClick={() => focusTechnician(location)}
                sx={{ gap: 1.5, borderRadius: "var(--defaultRadius)" }}
              >
                <FreshnessDot bucket={bucket} />
                <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {location.full_name}
                </Typography>
                {location.source === "job" && (
                  <MuiTooltip title={`${t("Location from the latest job")}: ${location.job_number}`}>
                    <ConstructionIcon fontSize="small" color="action" />
                  </MuiTooltip>
                )}
                <MuiTooltip title={formatDateTimeInline(location.recorded_at)}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {formatAge(location.recorded_at, now, t)}
                  </Typography>
                </MuiTooltip>
              </ListItemButton>
            );
          })}
        </List>
      )}
    </Box>
  );
};
